import {
  Form,
  Listbox,
  PostalCodeInputWithValidation,
} from "@talladega/components";
import GoogleMapsWrapper from "@modules/location/helpers/GoogleMapsWrapper";
import { Address } from "@common/types/Address";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import {
  FieldValues,
  Path,
  UseFormReturn,
  get,
  Controller,
  PathValue,
} from "react-hook-form";
import customerValidation from "@common/helpers/validations/customerValidationDeprecated";
import DeprecatedForm from "@common/components/form";
import { Province, provinces } from "@talladega/types";
import { TranslationKeys } from "src/@types/i18next";

type Props<TFields extends FieldValues> = {
  fieldsKey: string;
  required?: boolean;
  formMethods: UseFormReturn<TFields>;
};

export const focusOrder = [
  "firstName",
  "lastName",
  "streetAddress",
  "city",
  "province",
  "postalCode",
  "email",
];

const BuyerSellerInformationFormFields = <TFields extends FieldValues>({
  fieldsKey,
  required = false,
  formMethods,
}: Props<TFields>) => {
  const { t } = useTranslation(["common", "billOfSalePage"]);

  const {
    register,
    formState: { errors },
    control,
    getValues,
    setValue,
    clearErrors,
    trigger,
  } = formMethods;
  const errorsRoot = get(errors, fieldsKey);

  const [selectedProvince, setSelectedProvince] = useState<Province>();

  useEffect(() => {
    if (!getValues()) return;

    const province = getValues(`${fieldsKey}.province` as Path<TFields>);

    setSelectedProvince(province);

    setValue(
      fieldsKey as Path<TFields>,
      {
        firstName: getValues(`${fieldsKey}.firstName` as Path<TFields>),
        lastName: getValues(`${fieldsKey}.lastName` as Path<TFields>),
        streetAddress: getValues(`${fieldsKey}.streetAddress` as Path<TFields>),
        city: getValues(`${fieldsKey}.city` as Path<TFields>),
        postalCode: getValues(`${fieldsKey}.postalCode` as Path<TFields>),
        province,
        email: getValues(`${fieldsKey}.email` as Path<TFields>),
      } as PathValue<TFields, Path<TFields>>
    );
  }, [fieldsKey, getValues, setValue]);

  const onAutoCompleteChange = (address: Address | string) => {
    if (typeof address === "string") {
      setValue(
        fieldsKey as Path<TFields>,
        {
          firstName: getValues(`${fieldsKey}.firstName` as Path<TFields>),
          lastName: getValues(`${fieldsKey}.lastName` as Path<TFields>),
          streetAddress: address,
          city: getValues(`${fieldsKey}.city` as Path<TFields>),
          postalCode: getValues(`${fieldsKey}.postalCode` as Path<TFields>),
          province: getValues(`${fieldsKey}.province` as Path<TFields>),
          email: getValues(`${fieldsKey}.email` as Path<TFields>),
        } as PathValue<TFields, Path<TFields>>
      );
    } else {
      setValue(
        fieldsKey as Path<TFields>,
        {
          firstName: getValues(`${fieldsKey}.firstName` as Path<TFields>),
          lastName: getValues(`${fieldsKey}.lastName` as Path<TFields>),
          streetAddress: address.address1,
          city: address.city,
          postalCode: address.postalCode,
          province: provinces.find((p) => p.abbr === address.provinceAbbr),
          email: getValues(`${fieldsKey}.email` as Path<TFields>),
        } as PathValue<TFields, Path<TFields>>
      );
      setSelectedProvince(
        provinces.find((p) => p.abbr === address.provinceAbbr)
      );
      clearErrors([
        `${fieldsKey}.city` as Path<TFields>,
        `${fieldsKey}.postalCode` as Path<TFields>,
        `${fieldsKey}.province` as Path<TFields>,
      ]);
    }
  };

  const onClearAddressForm = () => {
    setSelectedProvince(undefined);
    setValue(
      fieldsKey as Path<TFields>,
      {
        firstName: getValues(`${fieldsKey}.firstName` as Path<TFields>),
        lastName: getValues(`${fieldsKey}.lastName` as Path<TFields>),
        streetAddress: "",
        city: "",
        postalCode: "",
        email: getValues(`${fieldsKey}.email` as Path<TFields>),
      } as PathValue<TFields, Path<TFields>>
    );
  };

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4">
        <Form.Input
          id={`${fieldsKey}FirstName`}
          autoComplete="given-name"
          className="w-full"
          maxLength={customerValidation.firstName.maxLength}
          label={t("common:first_name")}
          suppressHotjar
          feedback={errorsRoot?.firstName?.message}
          status={errorsRoot?.firstName ? "error" : "default"}
          {...register(`${fieldsKey}.firstName` as Path<TFields>, {
            ...customerValidation.firstName,
            required: { value: required, message: t("common:required") },
          })}
        />
        <Form.Input
          id={`${fieldsKey}LastName`}
          autoComplete="family-name"
          className="w-full"
          label={t("common:last_name")}
          maxLength={customerValidation.lastName.maxLength}
          suppressHotjar
          feedback={
            errorsRoot?.lastName?.type === "required"
              ? t("common:required")
              : ""
          }
          status={errorsRoot?.lastName ? "error" : "default"}
          {...register(`${fieldsKey}.lastName` as Path<TFields>, {
            ...customerValidation.lastName,
            required: { value: required, message: t("common:required") },
          })}
        />
      </div>
      <div className="flex justify-center items-center mb-4">
        <span className="text-center caption-3">
          {fieldsKey === "sellerInformation"
            ? t("billOfSalePage:please_enter_seller_name")
            : t("billOfSalePage:please_enter_buyer_name")}
        </span>
      </div>
      <GoogleMapsWrapper>
        <DeprecatedForm.AddressAutoComplete
          fieldError={errorsRoot?.streetAddress}
          label={t("common:street_address")}
          placeholder={t("common:search_address")}
          defaultValue={
            getValues(`${fieldsKey}.streetAddress` as Path<TFields>) || ""
          }
          onAddressSelect={onAutoCompleteChange}
          onAddressClear={onClearAddressForm}
          lookupType="address"
          showShortAddress
          {...register(`${fieldsKey}.streetAddress` as Path<TFields>, {
            maxLength: customerValidation.streetAddress.maxLength,
            required: { value: required, message: t("common:required") },
            onBlur: () =>
              trigger(`${fieldsKey}.streetAddress` as Path<TFields>),
          })}
        />
      </GoogleMapsWrapper>
      <div className="flex flex-col md:flex-row gap-4">
        <Form.Input
          id={`${fieldsKey}City`}
          autoComplete="address-level2"
          className="w-full"
          maxLength={customerValidation.city.maxLength}
          label={t("common:city")}
          suppressHotjar
          feedback={errorsRoot?.city?.message}
          status={errorsRoot?.city ? "error" : "default"}
          {...register(`${fieldsKey}.city` as Path<TFields>, {
            ...customerValidation.city,
            required: { value: required, message: t("common:required") },
          })}
        />

        <div className="w-full">
          <Controller
            control={control}
            rules={{
              required: { value: required, message: t("common:required") },
            }}
            name={`${fieldsKey}.province` as Path<TFields>}
            render={({ field: { onChange, ref } }) => (
              <Listbox
                items={provinces}
                keyOption={(item) => item.id}
                displayOption={(item) =>
                  item?.name ? t(item.name as TranslationKeys["common"]) : ""
                }
                displayButton={(item) =>
                  item?.name
                    ? t(item.name as TranslationKeys["common"])
                    : t("common:select_province")
                }
                valueOption={(item) => item}
                selectedItem={selectedProvince}
                label={t("common:province")}
                onChange={(selected) => {
                  setSelectedProvince(selected);
                  onChange(selected);
                }}
                feedback={errorsRoot?.province?.message}
                status={errorsRoot?.province ? "error" : "default"}
                defaultButtonClassName="text-slate-500"
                buttonRef={ref}
              />
            )}
          />
        </div>
        <PostalCodeInputWithValidation
          id={`${fieldsKey}.postalCode`}
          className="w-full"
          feedback={errorsRoot?.postalCode?.message}
          status={errorsRoot?.postalCode ? "error" : "default"}
          register={register}
          fieldKey={`${fieldsKey}.postalCode` as Path<TFields>}
          provinceToValidate={selectedProvince}
          required={required}
        />
      </div>

      <Form.Input
        id={`${fieldsKey}Email`}
        label={t("common:email")}
        type="email"
        autoComplete="email"
        maxLength={254}
        suppressHotjar
        defaultValue={getValues(`${fieldsKey}.email` as Path<TFields>) || ""}
        feedback={errorsRoot?.email?.message}
        status={errorsRoot?.email ? "error" : "default"}
        {...register(`${fieldsKey}.email` as Path<TFields>, {
          required: {
            value: required,
            message: t("common:required"),
          },
          maxLength: 254,
          pattern: {
            value: customerValidation.email.pattern,
            message: t("common:invalid_email"),
          },
        })}
        hidden={!required}
      />
    </>
  );
};

export default BuyerSellerInformationFormFields;
